/**
 * Admin paneli — tek sayfa, tab'lı. 6 bölüm:
 *   📋 Politika (tenant policy editor + versiyon geçmişi)
 *   💻 Cihazlar (liste + tag edit + disable)
 *   🔗 Oturumlar (aktif/geçmiş + bitir)
 *   📜 Denetim (audit log + CSV export)
 *   🔔 Webhook (subscription CRUD + test + events list)
 *   🚩 Feature flags (read-only diagnostics)
 */
import { appState } from '../state';
import { applyStyle, escapeHtml } from '../ui/css';
import { createCard } from '../ui/components/card';
import { createButton } from '../ui/components/button';
import { createPageHeader } from '../ui/components/pageHeader';
import { createTagBadge } from '../ui/components/badge';
import { icons } from '../ui/icons';
const TABS = [
    { id: 'policy', label: 'Politika', iconHtml: icons.shield() },
    { id: 'devices', label: 'Cihazlar', iconHtml: icons.monitor() },
    { id: 'sessions', label: 'Oturumlar', iconHtml: icons.users() },
    { id: 'audit', label: 'Denetim', iconHtml: icons.info() },
    { id: 'webhooks', label: 'Webhooks', iconHtml: icons.bell() },
    { id: 'flags', label: 'Flag\'ler', iconHtml: icons.settings() },
];
export function renderAdminPage() {
    const root = document.createElement('div');
    root.append(createPageHeader({
        title: 'Admin',
        highlight: 'paneli',
        subtitle: 'Tenant politikası, cihaz yönetimi, oturumlar, denetim ve webhook subscription\'ları.',
    }));
    // Tab bar
    const tabBar = document.createElement('div');
    applyStyle(tabBar, {
        display: 'flex',
        gap: '2px',
        padding: '4px',
        background: 'var(--bg-elev-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-sm)',
        marginBottom: 'var(--sp-4)',
        flexWrap: 'wrap',
        width: 'fit-content',
    });
    let currentTab = 'policy';
    for (const t of TABS) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.tab = t.id;
        btn.innerHTML = `${t.iconHtml}<span>${escapeHtml(t.label)}</span>`;
        applyStyle(btn, {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 14px',
            fontSize: 'var(--fs-caption)',
            fontWeight: '500',
            fontFamily: 'inherit',
            background: 'transparent',
            color: 'var(--text-secondary)',
            border: '1px solid transparent',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            transition: 'background var(--motion-fast), color var(--motion-fast)',
        });
        btn.addEventListener('click', () => {
            currentTab = t.id;
            updateTabActive();
            renderTab();
        });
        tabBar.append(btn);
    }
    root.append(tabBar);
    function updateTabActive() {
        tabBar.querySelectorAll('[data-tab]').forEach(b => {
            const active = b.dataset.tab === currentTab;
            b.style.background = active ? 'var(--accent-soft)' : 'transparent';
            b.style.color = active ? 'var(--accent)' : 'var(--text-secondary)';
            b.style.borderColor = active ? 'var(--accent)' : 'transparent';
            b.style.fontWeight = active ? '600' : '500';
        });
    }
    updateTabActive();
    const content = document.createElement('div');
    content.id = 'tab-content';
    applyStyle(content, { minHeight: '400px' });
    root.append(content);
    async function renderTab() {
        content.innerHTML = '<p style="color:var(--text-tertiary);font-size:var(--fs-caption)">Yükleniyor...</p>';
        try {
            switch (currentTab) {
                case 'policy':
                    await renderPolicy();
                    break;
                case 'devices':
                    await renderDevices();
                    break;
                case 'sessions':
                    await renderSessions();
                    break;
                case 'audit':
                    await renderAudit();
                    break;
                case 'webhooks':
                    await renderWebhooks();
                    break;
                case 'flags':
                    await renderFlags();
                    break;
            }
        }
        catch (e) {
            content.innerHTML = `<p style="color:var(--status-error);font-size:var(--fs-caption)">Hata: ${escapeHtml(String(e))}</p>`;
        }
    }
    // ============ POLICY ============
    async function renderPolicy() {
        const p = await appState.backend.getAdminPolicy();
        content.innerHTML = '';
        const card = createCard({
            caption: 'TENANT POLITIKASI',
            title: 'Oturum kuralları + güvenlik',
        });
        const body = document.createElement('div');
        applyStyle(body, { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', marginTop: 'var(--sp-3)' });
        body.innerHTML = `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--sp-3) var(--sp-4)">
        ${numField('f-sessionMax', 'Oturum max dakika', p.sessionMaxMinutes)}
        ${numField('f-retention', 'Kayıt saklama (gün)', p.recordingRetentionDays)}
        ${textField('f-cidr', 'İzinli CIDR\'lar (virgülle)', p.connectionAllowCidrs ?? '')}
        ${textField('f-countries', 'İzinli ülke kodları (TR,DE...)', p.allowedCountries ?? '')}
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--sp-2);margin-top:var(--sp-3)">
        ${checkbox('f-mfa-int', 'Interactive oturumda MFA zorunlu', p.requireMfaForInteractive)}
        ${checkbox('f-mfa-una', 'Unattended oturumda MFA zorunlu', p.requireMfaForUnattended)}
        ${checkbox('f-clip', 'Pano senk. KAPALI', p.disableClipboard)}
        ${checkbox('f-file', 'Dosya transferi KAPALI', p.disableFileTransfer)}
        ${checkbox('f-audio', 'Ses aktarımı KAPALI', p.disableAudio)}
        ${checkbox('f-force-rec', 'Kayıt zorunlu (force)', p.forceRecording)}
      </div>
    `;
        card.append(body);
        const actions = document.createElement('div');
        applyStyle(actions, { display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-4)' });
        const saveBtn = createButton({
            label: 'Politikayı Kaydet',
            variant: 'primary',
            iconHtml: icons.copy(),
        });
        const versionsBtn = createButton({
            label: 'Versiyon Geçmişi',
            variant: 'ghost',
            iconHtml: icons.refresh(),
        });
        actions.append(saveBtn, versionsBtn);
        card.append(actions);
        const versionsList = document.createElement('div');
        applyStyle(versionsList, { marginTop: 'var(--sp-4)' });
        card.append(versionsList);
        content.append(card);
        saveBtn.addEventListener('click', async () => {
            const body = {
                sessionMaxMinutes: Number(getInput('#f-sessionMax').value),
                recordingRetentionDays: Number(getInput('#f-retention').value),
                connectionAllowCidrs: getInput('#f-cidr').value || null,
                allowedCountries: getInput('#f-countries').value || null,
                requireMfaForInteractive: getInput('#f-mfa-int').checked,
                requireMfaForUnattended: getInput('#f-mfa-una').checked,
                disableClipboard: getInput('#f-clip').checked,
                disableFileTransfer: getInput('#f-file').checked,
                disableAudio: getInput('#f-audio').checked,
                forceRecording: getInput('#f-force-rec').checked,
            };
            try {
                await appState.backend.putAdminPolicy(body);
                alert('✓ Politika kaydedildi');
            }
            catch (e) {
                alert('Hata: ' + e);
            }
        });
        versionsBtn.addEventListener('click', async () => {
            const versions = await appState.backend.listPolicyVersions();
            versionsList.innerHTML = `
        <h3 style="margin:0 0 var(--sp-2);font-size:var(--fs-h3);color:var(--text-primary)">Versiyon geçmişi</h3>
        ${versions.length === 0
                ? '<p style="color:var(--text-tertiary);font-size:var(--fs-caption)">Henüz versiyon yok.</p>'
                : '<ul style="list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:6px">'
                    + versions.map(v => `<li style="padding:8px 12px;background:var(--bg-elev-3);border-radius:var(--radius-sm);font-size:var(--fs-caption);font-family:var(--font-mono)">
                  v${v.version} · ${escapeHtml(new Date(v.createdAt).toLocaleString())} · ${escapeHtml(v.createdByEmail ?? '?')}
                </li>`).join('')
                    + '</ul>'}
      `;
        });
    }
    // ============ DEVICES ============
    async function renderDevices() {
        const devices = await appState.backend.listAdminDevices();
        content.innerHTML = '';
        const filterRow = document.createElement('div');
        applyStyle(filterRow, { display: 'flex', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' });
        filterRow.innerHTML = `
      <input id="tag-filter" placeholder="Tag filtre (prod, ny-dc...)"
             style="flex:1;padding:10px 14px;font-size:var(--fs-caption);background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary)" />
    `;
        const filterBtn = createButton({
            label: 'Filtrele',
            variant: 'ghost',
            iconHtml: icons.search(),
        });
        filterRow.append(filterBtn);
        content.append(filterRow);
        const table = buildTable(['Human ID', 'Ad', 'OS', 'Tags', 'Son görüldü', '']);
        const tbody = table.querySelector('tbody');
        for (const d of devices)
            tbody.append(rowDevice(d));
        content.append(table);
        filterBtn.addEventListener('click', async () => {
            const tag = getInput('#tag-filter').value.trim();
            const rows = await appState.backend.listAdminDevices(tag || undefined);
            tbody.innerHTML = '';
            for (const d of rows)
                tbody.append(rowDevice(d));
        });
    }
    function rowDevice(d) {
        const tr = document.createElement('tr');
        tr.dataset.id = d.deviceId;
        const seen = d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString() : '—';
        tr.innerHTML = `
      <td>${tdMono(d.humanId)}</td>
      <td>${escapeHtml(d.displayName)}</td>
      <td>${escapeHtml(d.os)}</td>
      <td><input class="tag-edit" value="${escapeHtml(d.tags ?? '')}" style="width:160px;padding:4px 8px;font-size:var(--fs-caption);font-family:var(--font-mono);background:var(--bg-elev-3);border:1px solid var(--border-subtle);border-radius:4px;color:var(--text-primary)" /></td>
      <td style="font-size:var(--fs-tiny);color:var(--text-tertiary)">${escapeHtml(seen)}</td>
      <td style="text-align:right"></td>
    `;
        const tdAction = tr.querySelector('td:last-child');
        const tagSave = createButton({
            label: 'Kaydet',
            variant: 'subtle',
            size: 'sm',
            iconHtml: icons.copy(),
            onClick: async () => {
                const tag = tr.querySelector('.tag-edit').value;
                await appState.backend.setDeviceTags(d.deviceId, tag);
                tagSave.querySelector('span').textContent = '✓';
                setTimeout(() => { tagSave.querySelector('span').textContent = 'Kaydet'; }, 1500);
            },
        });
        const disable = createButton({
            label: 'Devre dışı',
            variant: 'subtle',
            size: 'sm',
            iconHtml: icons.trash(),
            onClick: async () => {
                if (!confirm('Cihazı devre dışı bırak?'))
                    return;
                await appState.backend.disableDevice(d.deviceId);
                tr.style.opacity = '0.4';
            },
        });
        disable.style.color = 'var(--status-error)';
        tdAction.append(tagSave, disable);
        return tr;
    }
    // ============ SESSIONS ============
    async function renderSessions() {
        const sessions = await appState.backend.listAdminSessions(true);
        content.innerHTML = '';
        const heading = document.createElement('h3');
        heading.textContent = `Aktif oturumlar (${sessions.length})`;
        applyStyle(heading, { margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-h3)', color: 'var(--text-primary)' });
        content.append(heading);
        const table = buildTable(['Session ID', 'Başlangıç', 'Rec', 'Bayt', '']);
        const tbody = table.querySelector('tbody');
        for (const s of sessions) {
            const tr = document.createElement('tr');
            tr.dataset.id = s.sessionId;
            tr.innerHTML = `
        <td>${tdMono(s.sessionId.slice(0, 12) + '…')}</td>
        <td style="color:var(--text-secondary)">${escapeHtml(new Date(s.startedAt).toLocaleString())}</td>
        <td></td>
        <td style="font-family:var(--font-mono);color:var(--text-secondary)">${(s.bytesTotal / 1024 / 1024).toFixed(1)} MB</td>
        <td style="text-align:right"></td>
      `;
            if (s.recordingEnabled) {
                tr.querySelector('td:nth-child(3)').append(createTagBadge('REC', 'error'));
            }
            const endBtn = createButton({
                label: 'Bitir',
                variant: 'subtle',
                size: 'sm',
                iconHtml: icons.trash(),
                onClick: async () => {
                    await appState.backend.endSession(s.sessionId);
                    tr.style.opacity = '0.4';
                },
            });
            endBtn.style.color = 'var(--status-error)';
            tr.querySelector('td:last-child').append(endBtn);
            tbody.append(tr);
        }
        content.append(table);
    }
    // ============ AUDIT ============
    async function renderAudit() {
        const events = await appState.backend.listAdminAudit(100);
        content.innerHTML = '';
        const filterRow = document.createElement('div');
        applyStyle(filterRow, { display: 'flex', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)', flexWrap: 'wrap' });
        filterRow.innerHTML = `
      <select id="evt-filter" style="padding:8px 12px;font-size:var(--fs-caption);background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary)">
        <option value="">(tümü)</option>
        <option>user.register</option><option>user.login</option><option>user.mfa_enabled</option>
        <option>pairing.consumed</option><option>session.started</option>
        <option>webauthn.enrolled</option><option>mdm.policy_changed</option><option>admin.manual_grant</option>
      </select>
    `;
        const refreshBtn = createButton({
            label: 'Yenile',
            variant: 'ghost',
            size: 'sm',
            iconHtml: icons.refresh(),
            onClick: () => renderAudit(),
        });
        const exportBtn = createButton({
            label: 'CSV indir',
            variant: 'ghost',
            size: 'sm',
            iconHtml: icons.download(),
            onClick: async () => {
                const et = getInput('#evt-filter').value;
                const blob = await appState.backend.exportAuditCsv(10000, et || undefined);
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `audit-${Date.now()}.csv`;
                a.click();
                URL.revokeObjectURL(url);
            },
        });
        filterRow.append(refreshBtn, exportBtn);
        content.append(filterRow);
        const table = buildTable(['Tarih', 'Olay', 'Actor', 'IP', 'Detay']);
        const tbody = table.querySelector('tbody');
        for (const e of events)
            tbody.append(rowAudit(e));
        content.append(table);
    }
    function rowAudit(e) {
        const tr = document.createElement('tr');
        tr.innerHTML = `
      <td style="white-space:nowrap;color:var(--text-secondary);font-size:var(--fs-tiny)">${escapeHtml(new Date(e.ts).toLocaleString())}</td>
      <td>${tdMono(e.eventType)}</td>
      <td style="font-family:var(--font-mono);font-size:var(--fs-tiny);color:var(--text-tertiary)">${escapeHtml(e.actorUserId?.slice(0, 8) ?? '—')}</td>
      <td style="color:var(--text-tertiary);font-size:var(--fs-tiny)">${escapeHtml(e.clientIp ?? '—')}</td>
      <td style="font-size:var(--fs-tiny);color:var(--text-tertiary);max-width:300px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(e.detailJson ?? '')}</td>
    `;
        return tr;
    }
    // ============ WEBHOOKS ============
    async function renderWebhooks() {
        let list = [];
        try {
            list = await appState.backend.listWebhooks();
        }
        catch (e) {
            content.innerHTML = `
        <div style="padding:var(--sp-5);background:var(--bg-elev-2);border:1px solid var(--status-error);border-radius:var(--radius-md);color:var(--text-secondary)">
          <strong style="color:var(--status-error)">${escapeHtml(String(e))}</strong>
          <p style="margin:var(--sp-2) 0 0;font-size:var(--fs-caption)">FEATURE_WEBHOOK_SUBSCRIPTIONS env var\'ı set edilmeli.</p>
        </div>
      `;
            return;
        }
        const events = await appState.backend.listWebhookEvents();
        content.innerHTML = '';
        const addCard = createCard({
            caption: 'YENİ WEBHOOK',
            title: 'Subscription ekle',
        });
        const addBody = document.createElement('div');
        applyStyle(addBody, { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', marginTop: 'var(--sp-3)' });
        addBody.innerHTML = `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--sp-3)">
        ${textField('wh-url', 'URL', '', 'https://...')}
        ${textField('wh-events', 'Events (CSV veya *)', '', '* | user.created,session.ended')}
      </div>
      ${textField('wh-label', 'Label (opsiyonel)', '')}
      <div style="font-size:var(--fs-tiny);color:var(--text-tertiary)">Destekli event'ler: ${events.map(escapeHtml).join(' · ')}</div>
    `;
        addCard.append(addBody);
        const addBtn = createButton({
            label: 'Ekle',
            variant: 'primary',
            iconHtml: icons.bell(),
        });
        const addRow = document.createElement('div');
        applyStyle(addRow, { marginTop: 'var(--sp-3)' });
        addRow.append(addBtn);
        addCard.append(addRow);
        const secretBox = document.createElement('div');
        secretBox.id = 'wh-secret';
        addCard.append(secretBox);
        applyStyle(addCard, { marginBottom: 'var(--sp-4)' });
        content.append(addCard);
        addBtn.addEventListener('click', async () => {
            try {
                const res = await appState.backend.createWebhook(getInput('#wh-url').value, getInput('#wh-events').value || '*', getInput('#wh-label').value || undefined);
                secretBox.innerHTML = `
          <div style="background:rgba(245,158,11,0.1);padding:var(--sp-3) var(--sp-4);margin-top:var(--sp-3);border:1px solid var(--status-busy);border-radius:var(--radius-sm)">
            <strong style="color:var(--status-busy)">Webhook Secret (bir daha gösterilmeyecek!):</strong>
            <pre style="background:var(--bg-elev-3);padding:var(--sp-2);margin:var(--sp-2) 0 0;user-select:all;font-family:var(--font-mono);font-size:var(--fs-caption);color:var(--text-primary);overflow-x:auto">${escapeHtml(res.secret)}</pre>
          </div>
        `;
                setTimeout(() => renderWebhooks(), 100);
            }
            catch (e) {
                alert('Hata: ' + e);
            }
        });
        const heading = document.createElement('h3');
        heading.textContent = `Mevcut webhook'lar (${list.length})`;
        applyStyle(heading, { margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-h3)', color: 'var(--text-primary)' });
        content.append(heading);
        const table = buildTable(['URL', 'Events', 'Fail', 'Son teslim', '']);
        const tbody = table.querySelector('tbody');
        for (const w of list) {
            const tr = document.createElement('tr');
            tr.dataset.id = w.id;
            tr.innerHTML = `
        <td style="max-width:280px;word-break:break-all">${tdMono(w.url)}</td>
        <td>${escapeHtml(w.events)}</td>
        <td>${w.failureCount}</td>
        <td style="color:var(--text-secondary);font-size:var(--fs-tiny)">${escapeHtml(w.lastDeliveryAt ? new Date(w.lastDeliveryAt).toLocaleString() : '—')}</td>
        <td style="text-align:right"></td>
      `;
            const tdAction = tr.querySelector('td:last-child');
            const testBtn = createButton({
                label: 'Test',
                variant: 'subtle',
                size: 'sm',
                onClick: async () => {
                    const r = await appState.backend.testWebhook(w.id);
                    alert(r.delivered ? `✓ Teslim edildi (${r.statusCode})` : `✗ Teslim edilemedi (${r.statusCode ?? 'hata'})`);
                },
            });
            const delBtn = createButton({
                label: 'Sil',
                variant: 'subtle',
                size: 'sm',
                iconHtml: icons.trash(),
                onClick: async () => {
                    if (!confirm('Webhook silinsin?'))
                        return;
                    await appState.backend.deleteWebhook(w.id);
                    renderWebhooks();
                },
            });
            delBtn.style.color = 'var(--status-error)';
            tdAction.append(testBtn, delBtn);
            tbody.append(tr);
        }
        content.append(table);
    }
    // ============ FLAGS ============
    async function renderFlags() {
        const f = await appState.backend.getFeatureFlags();
        content.innerHTML = `
      <p style="color:var(--text-secondary);font-size:var(--fs-caption);margin:0 0 var(--sp-3)">
        Ortam değişkenleriyle açılır/kapanır. Kodla değil, runtime'da.
      </p>
    `;
        const table = buildTable(['Flag', 'Aktif', 'Default']);
        const tbody = table.querySelector('tbody');
        for (const [k, v] of Object.entries(f.flags)) {
            const def = f.defaults[k];
            const diff = v !== def;
            const tr = document.createElement('tr');
            tr.innerHTML = `
        <td>${tdMono(`FEATURE_${k.toUpperCase()}`)}</td>
        <td></td>
        <td style="color:var(--text-tertiary);font-size:var(--fs-caption)">${def ? 'açık' : 'kapalı'}${diff ? ' (env override)' : ''}</td>
      `;
            tr.querySelector('td:nth-child(2)').append(createTagBadge(v ? 'AÇIK' : 'KAPALI', v ? 'success' : 'offline'));
            tbody.append(tr);
        }
        content.append(table);
    }
    function getInput(sel) {
        return content.querySelector(sel);
    }
    renderTab();
    return root;
}
// ─────────────────────────────────────────────────────────────────────────
// helpers
function buildTable(headers) {
    const t = document.createElement('table');
    applyStyle(t, {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 'var(--fs-caption)',
        background: 'var(--bg-elev-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
    });
    const thead = document.createElement('thead');
    thead.innerHTML = `<tr style="background:var(--bg-elev-3)">${headers.map(h => `<th style="padding:10px 14px;text-align:left;font-weight:600;color:var(--text-tertiary);font-size:var(--fs-tiny);letter-spacing:0.08em">${escapeHtml(h.toUpperCase())}</th>`).join('')}</tr>`;
    const tbody = document.createElement('tbody');
    // Tüm satırların hücreleri için aynı padding/border
    const styleTag = document.createElement('style');
    styleTag.textContent = `table tbody td { padding:10px 14px; border-top:1px solid var(--border-subtle); color:var(--text-primary); }`;
    t.append(styleTag, thead, tbody);
    return t;
}
function tdMono(value) {
    return `<code style="font-family:var(--font-mono);color:var(--accent);font-size:var(--fs-caption);letter-spacing:0.04em">${escapeHtml(value)}</code>`;
}
function numField(id, label, value) {
    return `
    <label style="display:flex;flex-direction:column;gap:6px;font-size:var(--fs-tiny);letter-spacing:0.08em;color:var(--text-tertiary);font-weight:500">
      ${escapeHtml(label.toUpperCase())}
      <input id="${id}" type="number" value="${escapeHtml(String(value))}"
             style="padding:8px 12px;font-size:var(--fs-body);font-family:var(--font-mono);background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary)" />
    </label>
  `;
}
function textField(id, label, value, placeholder = '') {
    return `
    <label style="display:flex;flex-direction:column;gap:6px;font-size:var(--fs-tiny);letter-spacing:0.08em;color:var(--text-tertiary);font-weight:500">
      ${escapeHtml(label.toUpperCase())}
      <input id="${id}" value="${escapeHtml(value)}" placeholder="${escapeHtml(placeholder)}"
             style="padding:8px 12px;font-size:var(--fs-caption);font-family:var(--font-mono);background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary)" />
    </label>
  `;
}
function checkbox(id, label, checked) {
    return `
    <label style="display:flex;align-items:center;gap:var(--sp-2);padding:8px 12px;background:var(--bg-elev-3);border:1px solid var(--border-subtle);border-radius:var(--radius-sm);cursor:pointer;font-size:var(--fs-caption);color:var(--text-primary)">
      <input type="checkbox" id="${id}" ${checked ? 'checked' : ''} style="accent-color:var(--accent)">
      ${escapeHtml(label)}
    </label>
  `;
}
